import { useEffect, useState, useRef, useMemo, forwardRef } from "react";
import { AgGridReact } from "ag-grid-react";

import Box from "@mui/material/Box"
import isEqual from "lodash/isEqual";

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import productionLimitService from "@plannel/services/mp/production-limit-service";

import { MPItemAutocomplete } from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog";
import DataState from "@plannel/components/aggrid/DataState";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";
import debug from "@plannel/utils/debug-util";
import reduxUtil from "@plannel/utils/redux-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import GridDatePicker from "@plannel/components/GridDatePicker";
import ResponseUtil from "@plannel/pages/utils/response-util";

const { limitType } = CodeUtil;
const { snackBarMessageColor } = ColorUtil;
const { httpStatusCode, responseCode } = ResponseUtil;
const { cycleUoms } = CodeUtil;

const ProductionLimit = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);
  const [gridPages, setGridPages] = useState(0);

  const [sortParams, setSortParams] = useState({});

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const itemSearch = useRef(reduxViewState?.itemSearch ?? "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [productionLimits, setProductionLimits] = useState([]);

  const DynamicGridDatePicker = forwardRef((props, ref) => {
    const dynamicParams = {
      dateType: props.dateType,
      maxDate: props.maxDate,
      minDate: props.minDate,
    };

    const mockedProps = {
      ...props,
      column: {
        ...props.column,
        colDef: {
          ...props.column.colDef,
          cellEditorParams: dynamicParams
        }
      }
    };

    return <GridDatePicker {...mockedProps} ref={ref} />;
  });

  const setItemCode = function (params) {
    params.data.item = { itemCd: params.newValue, name: "" };
    params.data.itemCd = params.newValue;
    params.data.supplier = null;
    params.data.supplierCd = null;
    params.data.supplierName = null;
    return true;
  }
  
  const columnDefs = [
    { headerName: "itemCd", field: "item.itemCd", colId: "itemCd", valueSetter: setItemCode, width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "item.name", colId: "itemName", type: ["nonEditableColumn"], width: 280, filterType: "string" },
    {
      headerName: "startDate", field: "startDate", colId: "startDate",
      type: ["defaultValueParser"],
      cellEditor: DynamicGridDatePicker,
      cellEditorParams: (params) => ({ dateType: "day", maxDate: params.data.endDate ? new Date(params.data.endDate) : null }),
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd"),
      width: 120, filterType: "date"
    },
    {
      headerName: "endDate", field: "endDate", colId: "endDate",
      type: ["defaultValueParser"],
      cellEditor: DynamicGridDatePicker,
      cellEditorParams: (params) => ({ dateType: "day", minDate: params.data.startDate ? new Date(params.data.startDate) : null }),
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM-dd"),
      width: 120, filterType: "date"
    },
    { headerName: "cycleCd", field: "cycleType", width: 100, cellClass: 'stringType', filterType: "string", filterOption: cycleUoms.map(uom => uom.text),
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: params => {
        const { value } = params;
        const uomIndex = cycleUoms.findIndex(uom => isEqual(uom.value, value));
        if (uomIndex > -1) {
          const cycleUom = cycleUoms[uomIndex];
          return t(cycleUom.text);
        }

        return value;
      },
      cellEditorParams: {
        formatValue: (value) => {
          const uom = cycleUoms.find(u => u.value === value);
          return uom ? t(uom.text) : (value ?? "");
        },
        values: cycleUoms.map(uom => uom.value)
      },
    },
    { headerName: "limitType", field: "limitType", colId: "limitType", width: 100, filterType: "string", filterOption: limitType.map(lt => lt.text),
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      valueFormatter: params => {
        const { value } = params;
        const ltIndex = limitType.findIndex(lt => isEqual(lt.value, value));
        if (ltIndex > -1) {
          const lt = limitType[ltIndex];
          return t(lt.value);
        }

        return value;
      },
      cellEditorParams: {
        formatValue: (value) => {
          const found = limitType.find(lt => lt.value === value);
          return found ? t(found.value) : (value ?? "");
        },
        values: limitType.map(lt => lt.value)
      },
    },
    { headerName: "limitValue", field: "limitValue", type: ["numberColumn", "rightAligned"], width: 120, filterType: "number" },
    { headerName: "bucketLimitFlg", field: "bucketLimitFlg", type: ["booleanColumn"], width: 100, filterType: "boolean" },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], width: 100, filterType: "boolean" },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 160, flex: 4, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (currentPage) {
      params.page = currentPage - 1;
    }

    if (pageSize) {
      params.pageSize = pageSize;
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveProductionLimits = () => {
    const params = getRequestParams();

    productionLimitService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setProductionLimits(results);
        setGridPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, currentPage, pageSize });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const saveProductionLimits = () => {
    gridRef.current.api.stopEditing(false);
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length > 0) {
      productionLimitService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const status = e?.response?.status;
          const errorDatas = e?.response?.data ?? {};
          const { message } = errorDatas;

          if (status === httpStatusCode.BadRequest) {
            for (const errorCode of Object.keys(errorDatas)) {
              if (errorCode === responseCode.NOT_FOUND_ITEM) {
                setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NotExistItemCodes", { itemCodes: errorDatas[errorCode] }) });
              } else {
                setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
              }
              break;
            }
          } else if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveProductionLimits();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const removeProductionLimits = () => {
    const ids = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(data => ids.push(data.id));

    if (ids.length > 0) {
      productionLimitService.remove(ids)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(e => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
          debug(e);
        })
        .then(() => {
          retrieveProductionLimits();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const addRow = () => {
    const newRow = { id: null, locCd: "", itemCd: "", bucketLimitFlg: false, activeFlg: true };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const collapseFilter = () => setOpenFilter(!openFilter);

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveProductionLimits();
    } else if (action === "DELETE") {
      removeProductionLimits();
    }
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveProductionLimits();
    }
  }

  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(gridRef.current?.api);
      retrieveProductionLimits();
    }

    return () => {
      setProductionLimits([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams])

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemSearch.current, extraItemCodesRef.current, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  const onGridReady = params => {
    setGridLoading(true);

    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  return (
    <>
      <FilterContainer>
        {/* <MPLocationTreeFilter viewName={viewName} filterValue={filterValue?.locations?.map(location => location?.locCd)} onChangeFilter={setFilterValue} /> */}
        <MPItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} key={"ITEM"} extraCodesRef={extraItemCodesRef} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={productionLimitService} columnDefs={columnDefs} params={getRequestParams(false)} />
          <AddButton onClick={addRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="production-limit" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={productionLimits}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        actionName={dialogInfo.actionName}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
    </>
  );
};

export default withTranslation()(ProductionLimit);